import { useCallback, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { GraduationCap, KeyRound } from 'lucide-react';
import { TelegramLoginWidget } from '../components/TelegramLoginWidget';
import { TELEGRAM_BOT_USERNAME } from '../lib/env';
import {
  useAuth,
  useTelegramLogin,
  useDevLogin,
  type TelegramAuthPayload,
} from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LangSwitcher } from '../components/LangSwitcher';
import { useT } from '../i18n';

export function Login() {
  const { data: user } = useAuth();
  const location = useLocation();
  const t = useT();
  const tgLogin = useTelegramLogin();
  const devLogin = useDevLogin();
  const [password, setPassword] = useState('');
  // Показываем форму dev-login по умолчанию. Backend сам отклонит запрос,
  // если NODE_ENV=production или DEV_LOGIN_PASSWORD не задан.
  const [showDevLogin, setShowDevLogin] = useState(true);

  const onAuth = useCallback(
    (payload: TelegramAuthPayload) => {
      tgLogin.mutate(payload);
    },
    [tgLogin],
  );

  if (user) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex justify-end">
          <LangSwitcher compact />
        </div>
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <GraduationCap className="h-10 w-10 text-slate-900" />
          <h1 className="text-xl font-semibold text-slate-900">{t('app.name')}</h1>
          <p className="text-sm text-slate-500">{t('login.subtitle')}</p>
        </div>

        {TELEGRAM_BOT_USERNAME ? (
          <TelegramLoginWidget botUsername={TELEGRAM_BOT_USERNAME} onAuth={onAuth} />
        ) : (
          <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            {t('login.botUsernameMissing')}
          </div>
        )}

        {tgLogin.isPending && (
          <p className="mt-4 text-center text-sm text-slate-500">{t('login.checking')}</p>
        )}
        {tgLogin.isError && (
          <p className="mt-4 text-center text-sm text-red-600">
            {tgLogin.error?.message ?? t('login.failed')}
          </p>
        )}

        {showDevLogin && (
          <>
            <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              {t('login.devDivider')}
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (password) devLogin.mutate({ password });
              }}
              className="space-y-3"
            >
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  placeholder={t('login.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <Button type="submit" loading={devLogin.isPending} disabled={!password} className="w-full">
                {t('login.byPassword')}
              </Button>
              {devLogin.isError && (
                <p className="text-center text-sm text-red-600">
                  {devLogin.error?.message ?? t('login.failed')}
                </p>
              )}
            </form>

            <button
              type="button"
              onClick={() => setShowDevLogin(false)}
              className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-600"
            >
              {t('login.hideDev')}
            </button>
          </>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">{t('login.adminsOnly')}</p>
      </div>
    </div>
  );
}
